import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config/apiconfig";

const API_URL = `${API_BASE_URL}/api`;

const BenefitsService = {
    getBenefits: async (companyId, token, userType) => {
        try {
            const response = await axios.get(`${API_URL}/benefits`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching benefits:", error);
            throw error;
        }
    },
    
    getEmployees: async (companyId, token, userType) => {
        try {
            const response = await axios.get(`${API_URL}/employees`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching employees:", error);
            throw error;
        }
    },
    
    addBenefit: async (benefit, companyId, token, userType) => {
        try {
            const response = await axios.post(`${API_URL}/benefits`, benefit, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error adding benefit:", error);
            throw error;
        }
    },
    
    deleteBenefit: async (id, companyId, token, userType) => {
        try {
            await axios.delete(`${API_URL}/benefits/${id}`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
        } catch (error) {
            console.error("Error deleting benefit:", error);
            throw error;
        }
    }
};

const EmployeeBenefits = () => {
    const { user, userType, companyId, logout } = useAuth();
    const [benefits, setBenefits] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [form, setForm] = useState({
        empNo: "",
        benefitType: "",
        amount: "",
        dateGranted: "",
        expiryDate: "",
    });

    const fetchBenefits = async () => {
        if (!companyId) {
            logout();
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const data = await BenefitsService.getBenefits(companyId, token, userType);
            setBenefits(data);
            setError(null);
        } catch (err) {
            console.error("Error fetching benefits:", err);
            setError("Failed to fetch benefits. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const fetchEmployees = async () => {
        if (!companyId) return;
        
        try {
            const token = localStorage.getItem('token');
            const data = await BenefitsService.getEmployees(companyId, token, userType);
            setEmployees(data);
        } catch (err) {
            console.error("Error fetching employees:", err);
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!companyId) {
            logout();
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await BenefitsService.addBenefit(form, companyId, token, userType);
            setForm({ 
                empNo: "", 
                benefitType: "", 
                amount: "", 
                dateGranted: "", 
                expiryDate: "" 
            });
            fetchBenefits();
        } catch (err) {
            setError("Failed to add benefit. Please try again.");
        }
    };

    const handleBenefitDelete = async (id) => {
        if (!companyId) {
            logout();
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await BenefitsService.deleteBenefit(id, companyId, token, userType);
            setBenefits(benefits.filter(benefit => benefit.id !== id));
        } catch (err) {
            setError("Failed to delete benefit. Please try again.");
        }
    };

    useEffect(() => {
        if (companyId) {
            fetchBenefits();
            fetchEmployees();
        }
    }, [companyId]);

    return (
        <div className="w-full p-6 space-y-6 bg-gradient-to-r from-blue-900 to-blue-700 min-h-screen">
            <h1 className="text-4xl font-bold text-white mb-6 text-center">Employee Benefits Management</h1>

            {companyId ? (
                <>
                    <form
                        onSubmit={handleFormSubmit}
                        className="bg-white p-6 rounded-xl shadow-lg mb-6 max-w-4xl mx-auto"
                    >
                        <h3 className="text-xl font-bold text-gray-700 mb-4">Add Benefit</h3>
                        
                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg mb-4">
                                {error}
                            </div>
                        )}
                        
                        <select
                            name="empNo"
                            value={form.empNo}
                            onChange={(e) => setForm({ ...form, empNo: e.target.value })}
                            required
                            className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Select Employee</option>
                            {employees.map((employee) => (
                                <option key={employee.id} value={employee.empNo}>
                                    {employee.name} ({employee.empNo})
                                </option>
                            ))}
                        </select>
                        
                        <input
                            name="benefitType"
                            placeholder="Benefit Type"
                            value={form.benefitType}
                            onChange={(e) => setForm({ ...form, benefitType: e.target.value })}
                            required
                            className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        
                        <input
                            type="number"
                            placeholder="Amount"
                            name="amount"
                            value={form.amount}
                            onChange={(e) => setForm({ ...form, amount: e.target.value })}
                            required
                            className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        
                        <div className="relative mb-6">
                            <input
                                id="dateGranted"
                                type="date"
                                name="dateGranted"
                                value={form.dateGranted}
                                onChange={(e) => setForm({ ...form, dateGranted: e.target.value })}
                                required
                                className="w-full p-3 pt-5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-transparent"
                                placeholder=" "
                            />
                            <label
                                htmlFor="dateGranted"
                                className={`absolute left-3 top-3 text-gray-400 text-sm transition-all ${
                                    form.dateGranted ? "-translate-y-3 scale-75" : ""
                                }`}
                            >
                                Date Granted
                            </label>
                        </div>

                        <div className="relative mb-6">
                            <input
                                id="expiryDate"
                                type="date"
                                name="expiryDate"
                                value={form.expiryDate}
                                onChange={(e) => setForm({ ...form, expiryDate: e.target.value })}
                                required
                                className="w-full p-3 pt-5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-transparent"
                                placeholder=" "
                            />
                            <label
                                htmlFor="expiryDate"
                                className={`absolute left-3 top-3 text-gray-400 text-sm transition-all ${
                                    form.expiryDate ? "-translate-y-3 scale-75" : ""
                                }`}
                            >
                                Expiry Date
                            </label>
                        </div>

                        <button
                            type="submit"
                            className="w-full bg-blue-600 text-white font-bold p-3 rounded-lg hover:bg-blue-700"
                        >
                            Add Benefit
                        </button>
                    </form>

                    <div className="bg-white p-6 rounded-xl shadow-lg max-w-4xl mx-auto">
                        <h3 className="text-xl font-bold text-gray-700 mb-4">Benefit Record</h3>
                        {loading ? (
                            <div className="text-center py-4">Loading benefits data...</div>
                        ) : (
                            <div className="overflow-x-auto max-h-[400px] scrollbar-thin scrollbar-thumb-blue-600 scrollbar-track-gray-200">
                                <table className="w-full min-w-max whitespace-nowrap border-collapse bg-white rounded-lg shadow-md">
                                    <thead>
                                        <tr className="bg-blue-600 text-white">
                                            <th className="p-3 text-left font-semibold">Employee No.</th>
                                            <th className="p-3 text-left font-semibold">Employee Name</th>
                                            <th className="p-3 text-left font-semibold">Benefit Type</th>
                                            <th className="p-3 text-left font-semibold">Amount</th>
                                            <th className="p-3 text-left font-semibold">Date Granted</th>
                                            <th className="p-3 text-left font-semibold">Expiry Date</th>
                                            <th className="p-3 text-center font-semibold">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {benefits.length > 0 ? (
                                            benefits.map((benefit) => {
                                                const employee = employees.find(emp => emp.empNo === benefit.empNo);
                                                return (
                                                    <tr
                                                        key={benefit.id}
                                                        className="odd:bg-gray-50 even:bg-gray-100 hover:bg-gray-200"
                                                    >
                                                        <td className="p-3 border-b border-gray-200">{benefit.empNo}</td>
                                                        <td className="p-3 border-b border-gray-200">{employee ? employee.name : "Unknown"}</td>
                                                        <td className="p-3 border-b border-gray-200">{benefit.benefitType}</td>
                                                        <td className="p-3 border-b border-gray-200">{benefit.amount}</td>
                                                        <td className="p-3 border-b border-gray-200">{new Date(benefit.dateGranted).toLocaleDateString()}</td>
                                                        <td className="p-3 border-b border-gray-200">{new Date(benefit.expiryDate).toLocaleDateString()}</td>
                                                        <td className="p-3 text-center border-b border-gray-200">
                                                            <button
                                                                onClick={() => handleBenefitDelete(benefit.id)}
                                                                className="bg-red-500 text-white px-3 py-1 rounded-lg hover:bg-red-600 transition duration-300"
                                                            >
                                                                Delete
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="p-3 text-center text-gray-500">
                                                    No benefits records found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <div className="text-center text-lg text-white">
                    Company information not available. Please login again.
                </div>
            )}
        </div>
    );
};

export default EmployeeBenefits;